/// <amd-module name="VSS/Features/Accounts/AccountPickerProvider" />
import { IVssPageContext } from "VSS/Platform/Context";

import { IPickListAction, IPickListItem, IItemPickerProvider } from "VSSUI/Components/PickList";
import { VssIconType } from "VSSUI/VssIcon";

import { IOrganization, IOrganizationsService } from "VSS/Features/Services/OrganizationsService";
import * as AccountResources from "VSS/Features/Accounts/Resources";

export interface IAccountPickerOptions {
    /**
     * The id of the initially selected account
     */
    initialAccountId?: string;

    /**
     * The name of the initially selected accout
     */
    initialAccountName?: string;

    /**
     * Optional delegate to get the href property of the pick list item
     */
    getItemHref?: (accountItem: IOrganization) => string;

    /**
     * Optional list of actions to render after the accounts in the list
     */
    actions?: IPickListAction[];

    /**
     * Callback invoked whenever a new account is selected
     */
    onAccountSelected?: (selectedAccount: IOrganization) => void | boolean;

    /**
     * Header text for the default group
     */
    defaultGroupHeader?: string;

    /**
     * Header text for the favorites group
     */
    favoriteGroupHeader?: string;
}

/**
 * Provides items for a picker which shows accounts
 */
export class AccountPickerProvider implements IItemPickerProvider<IOrganization> {
    private _pageContext: IVssPageContext;
    private _options: IAccountPickerOptions;

    private _artifactsPromise: Promise<IOrganization[]>;
    private _resolvedArtifacts: IOrganization[];
    private _selectedItem: IOrganization | undefined;
    private _handledInitialSelection: boolean;
    private _disposed = false;

    constructor(pageContext: IVssPageContext, options?: IAccountPickerOptions) {
        this._pageContext = pageContext;
        this._options = options || {};
    }

    public getTitleTextForItem(item: IOrganization): string {
        return item.name;
    }

    /**
     * Get list of items to be displayed in favorites section
     */
    public getItems = (): Promise<IOrganization[]> => {
        if (!this._artifactsPromise) {
            const accountsService = this._pageContext.getService<IOrganizationsService>("IOrganizationsService");
            this._artifactsPromise = accountsService.getUserOrganizations();
        }
        return this._artifactsPromise;
    };

    /**
     * Returns a PickListItem for the specified item
     */
    public getListItem = (item: IOrganization): IPickListItem => {
        const { getItemHref } = this._options;

        const listItem: IPickListItem = {
            key: item.id,
            name: item.name,
            iconProps: {
                iconType: VssIconType.fabric,
                iconName: "Work"
            }
        };

        if (getItemHref) {
            listItem.href = getItemHref(item);
        }

        return listItem;
    };

    /**
     * The currently selected item in the picker.
     */
    public get selectedItem(): IOrganization | undefined {
        if (!this._handledInitialSelection) {
            this._handledInitialSelection = true;
            this._selectedItem = undefined;
            if (this._options.initialAccountId && this._options.initialAccountName) {
                this._selectedItem = {
                    id: this._options.initialAccountId,
                    name: this._options.initialAccountName,
                    url: ""
                };
            }
        }
        return this._selectedItem;
    }

    /**
     * Sets the selected item for the existing picker.
     */
    public setSelectedItem(artifact: IOrganization): void {
        this._selectedItem = artifact;
    }

    /**
     * Handler called when the selected item changes
     */
    public onSelectedItemChanged = (artifact: IOrganization): void | boolean => {
        const { onAccountSelected } = this._options;
        if (onAccountSelected) {
            return onAccountSelected(artifact);
        } else if (!this._options.getItemHref && artifact.url) {
            window.location.href = artifact.url;
            return true;
        }
    };

    /**
     * Optional list of actions to render after all items and groups
     */
    public get actions(): IPickListAction[] {
        return this._options.actions || [];
    }

    /**
     * Whether or not the search box should be shown for the PickList
     */
    public get isSearchable(): boolean {
        return true;
    }

    /**
     * Placeholder text used in the search TextField
     */
    public get searchTextPlaceholder(): string | undefined {
        return AccountResources.FilterAccounts;
    }

    /**
     * Text to display when there are no results (based on the current search criteria)
     */
    public get searchNoResultsText(): string | undefined {
        return AccountResources.NoAccountSearchResults;
    }
}
